import React from "react";
import { Modal, Button, Alert } from "@arco-design/web-react";

// needsDomainChoice: whether to offer setting up the address's domain before
// adding it as a sender. advice is from /api/settings/sending-domains/advice.
export function needsDomainChoice(advice) {
  return !!advice && (advice.status === "none" || advice.status === "pending");
}

// DomainChoiceDialog asks, before a sender on the user's own domain is added,
// whether to set up the domain (every address on it works, no confirm emails)
// or verify just this one address. For a domain already being set up, the
// first choice is to wait for it.
export default function DomainChoiceDialog({ choice, busy, error, onDomain, onAddress, onClose }) {
  const advice = (choice && choice.advice) || {};
  const email = (choice && choice.data && choice.data.email) || "";
  const domain = advice.domain || "";
  const pending = advice.status === "pending";
  const strict = !!advice.strict_dmarc;

  return (
    <Modal
      visible={!!choice}
      title={pending ? `${domain} is being set up` : `Set up ${domain} instead?`}
      onCancel={onClose}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={onAddress} disabled={!!busy} loading={busy === "address"}>
            Just verify this address
          </Button>
          <Button type="primary" onClick={onDomain} disabled={!!busy} loading={busy === "domain"}>
            {pending ? `Wait for ${domain}` : `Set up ${domain}`}
          </Button>
        </div>
      }
    >
      {pending ? (
        <p style={{ marginTop: 0 }}>
          You've added {domain} under Sending Domains, but its DNS records haven't been found yet.
          Once they are, {email} is ready to send with no confirm emails.
        </p>
      ) : (
        <p style={{ marginTop: 0 }}>
          Set up {domain} once and every address on it can send, with no confirm emails.
          Your mail is also less likely to land in spam. You'll need to add a few DNS records
          where your domain is managed; we'll show you which ones.
        </p>
      )}
      <p>
        Or verify just {email}: we'll send it two emails (one from us, one from Amazon), and it's
        ready once both links are clicked.
      </p>
      {strict && (
        <Alert
          type="warning"
          content={`${domain} tells inboxes to junk or reject mail it didn't sign. Mail from ${email} verified on its own isn't signed by ${domain}, so it will probably be blocked or land in spam. Setting up the domain fixes this.`}
        />
      )}
      {error && <div style={{ fontSize: 12, color: "rgb(var(--red-6))", marginTop: 8 }}>{error}</div>}
    </Modal>
  );
}
